import {BaseApi} from './BaseApi'
import type {
  AssignmentScope,
  CategoryColor,
  CategoryIcon,
  CreateCategoryInput,
  PolicyCoverageTotals,
  PolicyEmployeesQuery,
  PolicyEmployeesResponse,
  PolicyStatus,
  PolicyType,
  ResolveOverrideInput,
  ResolveOverrideResult,
  TimeOffCategory,
  TimeOffPolicy,
  TimeOffSummaryMetrics,
} from '../components/platform/global/time-off/types'
import type {AccrualCanvasDraft} from '../components/platform/global/time-off/create-policy/accrualCalculations'
import type {AssignmentDraft} from '../components/platform/global/time-off/create-policy/assignmentHelpers'
import type {CreatePolicyDraft} from '../components/platform/global/time-off/create-policy/wizardTypes'
import type {
  AssignmentTimeOffState,
  TimeOffBalanceCard,
  TimeOffHistoryEntry,
} from '../components/msp/manage-job-orders/job-order-summary/candidate-details/candidate-assignment-history/assignment-time-off/types'
import {deduplicateTimeOffHistory} from '../components/msp/manage-job-orders/job-order-summary/candidate-details/candidate-assignment-history/assignment-time-off/filterHelpers'
import {reportCatchError} from '../utils/errorReporter'

/** Assignment Time Off tab payload from GET /time-off-assignment/summary. */
export interface AssignmentTimeOffSummaryApiModel {
  assignmentId: string
  applicantId: string
  balances: TimeOffBalanceCard[]
  history: TimeOffHistoryEntry[]
  /**
   * STFFLG-4310: the assignment's hire date as a UTC 'YYYY-MM-DD' (`assignmentStartDate`, else
   * `startDate`); null when unknown. An msp before STFFLG-4310 omits it, which reads as null.
   */
  hireDate?: string | null
}

export interface ProjectTimeOffBalanceRequest {
  assignmentId: string
  applicantId?: string
  categoryId: string
  asOfDate: string
}

export interface ProjectTimeOffBalanceResponse {
  employee: {
    applicantId: string
    assignmentId: string
    name?: string | null
    startDate: string
  }
  policy: {
    id: string
    name: string
    type: string
    categoryId: string
    categoryName: string
  }
  asOfDate: string
  projectedBalance: number | null
  currentMilestone?: string | null
  note?: string
  details: Array<{
    date: string
    dateIso: string
    action: string
    hoursEarned: number | null
    balance: number | null
  }>
}

export const mapAssignmentTimeOffSummary = (
  data: AssignmentTimeOffSummaryApiModel,
): AssignmentTimeOffState => ({
  balances: data.balances ?? [],
  history: deduplicateTimeOffHistory(data.history ?? []),
})

/** API category document (matches msp-service TimeOffCategoryResponse). */
export interface TimeOffCategoryApiModel {
  id: string
  name: string
  description: string
  icon: CategoryIcon
  color: CategoryColor
  isCustom: boolean
  status?: string
  policyCount?: number
}

/** API policy document (matches msp-service TimeOffPolicyResponse). */
export interface TimeOffPolicyApiModel {
  id: string
  name: string
  categoryId: string
  type: PolicyType
  annualUsageLimitHours?: number | null
  status: PolicyStatus
  applyToAllStates: boolean
  states: string[]
  summary: string
  settings: TimeOffPolicy['settings']
  scope: AssignmentScope | null
  employeesCovered: number
  employeesCoveredBeforeArchive?: number | null
  archivedAt?: string | null
  archivedNote?: string | null
  accrual?: AccrualCanvasDraft | null
  milestones: TimeOffPolicy['milestones']
  assignments: Array<{
    id?: string
    entityId: string
    entityName: string
    scope: AssignmentScope
    employees: number
    /**
     * STFFLG-4311: when the row was assigned (ISO), set by the server; null on rows saved before
     * it. At the same scope the newest assignment wins. Never sent back: the server derives it.
     */
    assignedAt?: string | null
  }>
  /**
   * STFFLG-4311: the document version. Each save raises it, and so does a newer policy taking
   * some of this one's rows; PUT /time-off-policy/:id with an older one is a 409.
   */
  versionKey?: number
}

export interface CreateTimeOffPolicyApiRequest {
  name: string
  categoryId: string
  type: PolicyType
  annualUsageLimitHours?: number | null
  applyToAllStates: boolean
  states: string[]
  accrual?: AccrualCanvasDraft
  scope?: AssignmentScope | null
  assignments: Array<{
    entityId: string
    entityName: string
    scope: AssignmentScope
    employees: number
  }>
  employeesCovered?: number
}

/** PUT /time-off-policy/:id body: the create body plus the version the policy was loaded at. */
export interface UpdateTimeOffPolicyApiRequest extends CreateTimeOffPolicyApiRequest {
  versionKey?: number
}

const toApiAssignments = (
  assignment: AssignmentDraft,
): CreateTimeOffPolicyApiRequest['assignments'] => {
  if (!assignment.scopeType || assignment.selectedIds.length === 0) return []
  const scope = assignment.scopeType
  const entities =
    assignment.selectedEntities.length > 0
      ? assignment.selectedEntities
      : assignment.selectedIds.map((id) => ({id, name: id, employees: 0}))

  return entities.map((entity) => ({
    entityId: entity.id,
    entityName: entity.name,
    scope,
    employees: entity.employees || (scope === 'Employee' ? 1 : 0),
  }))
}

/** Maps a Create Policy wizard draft to the POST /time-off-policy body. */
export const draftToCreatePolicyRequest = (
  draft: CreatePolicyDraft,
): CreateTimeOffPolicyApiRequest => {
  const type =
    draft.type === 'Flexible' || draft.type === 'Manual' || draft.type === 'Accrual'
      ? draft.type
      : 'Accrual'
  return {
    name: draft.name.trim(),
    categoryId: draft.categoryId,
    type,
    annualUsageLimitHours:
      type === 'Flexible' || type === 'Manual' ? draft.annualUsageLimitHours : undefined,
    applyToAllStates: draft.applyToAllStates,
    states: draft.applyToAllStates ? [] : draft.states,
    accrual: type === 'Accrual' ? draft.accrual : undefined,
    scope: draft.assignment.scopeType || null,
    // Coverage is computed live by the API; employeesCovered is no longer sent.
    assignments: toApiAssignments(draft.assignment),
  }
}

export const mapCategoryFromApi = (category: TimeOffCategoryApiModel): TimeOffCategory => ({
  id: category.id,
  name: category.name,
  description: category.description,
  icon: category.icon,
  color: category.color,
  isCustom: category.isCustom,
})

export const mapPolicyFromApi = (policy: TimeOffPolicyApiModel): TimeOffPolicy => ({
  id: policy.id,
  name: policy.name,
  categoryId: policy.categoryId,
  type: policy.type,
  annualUsageLimitHours: policy.annualUsageLimitHours ?? null,
  // An unassigned policy has no scope (msp stores null or omits it); never present it as EOR.
  scope: policy.scope ?? null,
  employeesCovered: policy.employeesCovered,
  status: policy.status,
  summary: policy.summary,
  settings: {
    ...policy.settings,
    annualUsageLimit:
      policy.settings.annualUsageLimit ??
      (policy.type === 'Flexible' || policy.type === 'Manual'
        ? policy.annualUsageLimitHours != null && policy.annualUsageLimitHours > 0
          ? `${policy.annualUsageLimitHours} hours per year`
          : 'No annual usage limit'
        : 'Not applicable'),
  },
  milestones: policy.milestones ?? [],
  assignments: (policy.assignments ?? []).map((assignment) => ({
    id: assignment.id || assignment.entityId,
    entityId: assignment.entityId || assignment.id,
    entityName: assignment.entityName,
    scope: assignment.scope,
    employees: assignment.employees,
  })),
  employees: [],
  excludedEmployees: [],
  archivedAt: policy.archivedAt ?? undefined,
  archivedNote: policy.archivedNote ?? undefined,
  employeesCoveredBeforeArchive: policy.employeesCoveredBeforeArchive ?? undefined,
  ...(typeof policy.versionKey === 'number' && {versionKey: policy.versionKey}),
})

export interface CreateTimeOffRequestPayload {
  applicantId: string
  assignmentId: string
  policyId: string
  startDate: string
  endDate: string
  dailyHours: number[]
  note?: string
}

export interface TimeOffRequestApiModel {
  id: string
  applicantId: string
  assignmentId: string
  policyId: string
  categoryId: string
  typeName: string
  status: 'Pending' | 'Approved' | 'Denied' | 'Cancelled'
  startDateIso: string
  endDateIso: string
  dailyHours: number[]
  totalHours: number
  note?: string | null
  externalRequestId: string
  ronosSync?: {
    status: 'Pending' | 'Synced' | 'Failed' | 'NotApplicable'
    error?: string | null
    externalRequestId?: string | null
    lastSyncAt?: string | null
  }
}

/** Best-effort API/axios error message for snackbars. */
export const getTimeOffApiErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== 'object') return fallback
  const maybeAxios = error as {
    response?: {data?: {message?: string | string[]}}
    message?: string
  }
  const apiMessage = maybeAxios.response?.data?.message
  if (Array.isArray(apiMessage) && apiMessage.length) return apiMessage.join(', ')
  if (typeof apiMessage === 'string' && apiMessage.trim()) return apiMessage
  if (typeof maybeAxios.message === 'string' && maybeAxios.message.trim()) {
    return maybeAxios.message
  }
  return fallback
}

/**
 * Inclusive calendar-day count (UTC date parts) for building dailyHours from a total.
 */
export const inclusiveDayCount = (startDate: string, endDate: string): number => {
  const start = new Date(`${startDate}T00:00:00Z`)
  const end = new Date(`${endDate}T00:00:00Z`)
  const diff = Math.floor((end.getTime() - start.getTime()) / 86_400_000) + 1
  return diff
}

/**
 * Expand a single total into a Ronos/Staffology `dailyHours` array.
 *
 * Backend contract (msp-service `distributeTotalHours`): put the full total on
 * day 0 and zeros on remaining calendar days. Length must equal inclusive
 * calendar days in [startDate, endDate]. Each entry must be 0–24, so
 * `totalHours` must be ≤ 24 when using this helper.
 */
export const buildDailyHoursFromTotal = (
  startDate: string,
  endDate: string,
  totalHours: number,
): number[] => {
  const days = inclusiveDayCount(startDate, endDate)
  if (days < 1) {
    throw new Error('endDate must be on or after startDate')
  }
  if (totalHours > 24) {
    throw new Error('totalHours must be 24 or less when expanding to dailyHours')
  }
  const hours = Array.from({length: days}, () => 0)
  hours[0] = Math.round(totalHours * 100) / 100
  return hours
}

/**
 * Global Time Off Policies catalogue API (distinct from entity-scoped timeOffApi).
 *
 * Nest controllers are mounted without an `/api` prefix; BaseApi's VITE_BACKEND_URL
 * already includes `/api` when talking to the gateway.
 */
class TimeOffPoliciesApi extends BaseApi {
  private readonly categoryUrl = '/time-off-category'
  private readonly policyUrl = '/time-off-policy'
  /** Assignment Time Off tab; open to agency users for their own clients (403 otherwise). */
  private readonly assignmentUrl = '/time-off-assignment'
  private readonly requestUrl = '/time-off-request'

  async listCategories(): Promise<TimeOffCategory[]> {
    try {
      const response = await this.client.get<TimeOffCategoryApiModel[]>(`${this.categoryUrl}/list`)
      return (response.data ?? []).map(mapCategoryFromApi)
    } catch (error) {
      reportCatchError('Error listing time-off categories:', error)
      throw error
    }
  }

  async createCategory(input: CreateCategoryInput): Promise<TimeOffCategory> {
    try {
      const response = await this.client.post<TimeOffCategoryApiModel>(this.categoryUrl, {
        name: input.name.trim(),
        description: input.description.trim() || 'Custom Time Off category',
        icon: input.icon,
        color: input.color,
      })
      return mapCategoryFromApi(response.data)
    } catch (error) {
      reportCatchError('Error creating time-off category:', error)
      throw error
    }
  }

  async listPolicies(params?: {
    status?: PolicyStatus
    categoryId?: string
  }): Promise<TimeOffPolicy[]> {
    try {
      const query = new URLSearchParams()
      if (params?.status) query.append('status', params.status)
      if (params?.categoryId) query.append('categoryId', params.categoryId)
      const suffix = query.toString() ? `?${query.toString()}` : ''
      const response = await this.client.get<TimeOffPolicyApiModel[]>(
        `${this.policyUrl}/list${suffix}`,
      )
      return (response.data ?? []).map(mapPolicyFromApi)
    } catch (error) {
      reportCatchError('Error listing time-off policies:', error)
      throw error
    }
  }

  async getSummary(): Promise<TimeOffSummaryMetrics> {
    try {
      const response = await this.client.get<TimeOffSummaryMetrics>(`${this.policyUrl}/summary`)
      return response.data
    } catch (error) {
      reportCatchError('Error fetching time-off policy summary:', error)
      throw error
    }
  }

  /**
   * Estimate employee coverage for Assign-step selections.
   * POST /time-off-policy/assignment-headcounts
   */
  async getAssignmentHeadcounts(
    scope: AssignmentScope,
    entityIds: string[],
  ): Promise<Record<string, number>> {
    try {
      const uniqueIds = [...new Set(entityIds.filter(Boolean))]
      if (uniqueIds.length === 0) return {}
      const response = await this.client.post<{
        scope: AssignmentScope
        counts: Array<{entityId: string; employees: number}>
      }>(`${this.policyUrl}/assignment-headcounts`, {
        scope,
        entityIds: uniqueIds,
      })
      const map: Record<string, number> = {}
      for (const row of response.data?.counts ?? []) {
        map[row.entityId] = row.employees ?? 0
      }
      return map
    } catch (error) {
      reportCatchError('Error fetching time-off assignment headcounts:', error)
      throw error
    }
  }

  /**
   * Distinct candidates a draft's assignments would govern once saved: overlaps
   * de-duplicated and more specific same-category policies applied, so it equals
   * the `employeesCovered` the saved policy shows.
   * POST /time-off-policy/coverage-preview
   */
  async previewCoverage(params: {
    categoryId: string
    assignments: Array<{entityId: string; scope: AssignmentScope}>
    excludePolicyId?: string
  }): Promise<PolicyCoverageTotals> {
    try {
      const response = await this.client.post<{
        total: number
        withActive: number
        withoutActive: number
      }>(`${this.policyUrl}/coverage-preview`, params)
      return {
        all: response.data.total,
        withActive: response.data.withActive,
        withoutActive: response.data.withoutActive,
      }
    } catch (error) {
      reportCatchError('Error previewing time-off policy coverage:', error)
      throw error
    }
  }

  async createPolicy(draft: CreatePolicyDraft): Promise<TimeOffPolicy> {
    try {
      const body = draftToCreatePolicyRequest(draft)
      const response = await this.client.post<TimeOffPolicyApiModel>(this.policyUrl, body)
      return mapPolicyFromApi(response.data)
    } catch (error) {
      reportCatchError('Error creating time-off policy:', error)
      throw error
    }
  }

  /**
   * Full policy document by id. Returns the raw API model (with `accrual`,
   * `states`, and assignment entity ids) so the wizard can rebuild an editable
   * draft — the list/cache shape is lossy.
   */
  async getPolicyById(policyId: string): Promise<TimeOffPolicyApiModel> {
    try {
      const response = await this.client.get<TimeOffPolicyApiModel>(`${this.policyUrl}/${policyId}`)
      return response.data
    } catch (error) {
      reportCatchError('Error fetching time-off policy:', error)
      throw error
    }
  }

  async listPolicyEmployees(
    policyId: string,
    params: PolicyEmployeesQuery,
  ): Promise<PolicyEmployeesResponse> {
    try {
      const query = new URLSearchParams({
        page: String(params.page),
        pageSize: String(params.pageSize),
      })
      if (params.search) query.append('search', params.search)
      if (params.inheritedFrom) query.append('inheritedFrom', params.inheritedFrom)
      if (params.assignmentStatus) query.append('assignmentStatus', params.assignmentStatus)
      const response = await this.client.get<PolicyEmployeesResponse>(
        `${this.policyUrl}/${policyId}/employees?${query.toString()}`,
      )
      return response.data
    } catch (error) {
      reportCatchError('Error fetching time-off policy employees:', error)
      throw error
    }
  }

  async exportPolicyEmployees(
    policyId: string,
    params: Pick<PolicyEmployeesQuery, 'search' | 'inheritedFrom' | 'assignmentStatus'>,
  ): Promise<Blob> {
    try {
      const query = new URLSearchParams()
      if (params.search) query.append('search', params.search)
      if (params.inheritedFrom) query.append('inheritedFrom', params.inheritedFrom)
      if (params.assignmentStatus) query.append('assignmentStatus', params.assignmentStatus)
      const suffix = query.toString() ? `?${query.toString()}` : ''
      const response = await this.client.get<Blob>(
        `${this.policyUrl}/${policyId}/employees/export${suffix}`,
        {responseType: 'blob'},
      )
      return response.data
    } catch (error) {
      reportCatchError('Error exporting time-off policy employees:', error)
      throw error
    }
  }

  /**
   * PUT /time-off-policy/:id. Sends the draft's `versionKey` (the version the policy was loaded
   * at) when it has one, so a save from an outdated copy is a 409 instead of taking back rows a
   * newer policy took (STFFLG-4311). That 409 (`errorCode` TIME_OFF_POLICY_STALE) carries a
   * `message` telling the admin to reload, which the snackbar shows as is. The returned policy
   * carries the new version, which the next save of the same draft must send.
   */
  async updatePolicy(policyId: string, draft: CreatePolicyDraft): Promise<TimeOffPolicy> {
    try {
      const body: UpdateTimeOffPolicyApiRequest = {
        ...draftToCreatePolicyRequest(draft),
        ...(typeof draft.versionKey === 'number' && {versionKey: draft.versionKey}),
      }
      const response = await this.client.put<TimeOffPolicyApiModel>(
        `${this.policyUrl}/${policyId}`,
        body,
      )
      return mapPolicyFromApi(response.data)
    } catch (error) {
      reportCatchError('Error updating time-off policy:', error)
      throw error
    }
  }

  /**
   * POST /time-off-policy/:id/overrides/resolve. Settles one excluded candidate's conflict:
   * `assign` adds a Candidate-level row for them on this policy, `keep` pins them on the policy
   * that wins today. Both record the decision and its note. A 409 means the candidate is no longer
   * excluded (someone else already changed it); the caller refetches the roster.
   */
  async resolveOverride(
    policyId: string,
    input: ResolveOverrideInput,
  ): Promise<ResolveOverrideResult> {
    try {
      const response = await this.client.post<ResolveOverrideResult>(
        `${this.policyUrl}/${policyId}/overrides/resolve`,
        input,
      )
      return response.data
    } catch (error) {
      reportCatchError('Error resolving time-off policy override:', error)
      throw error
    }
  }

  async archivePolicy(policyId: string, note: string, archivedAt?: string): Promise<TimeOffPolicy> {
    try {
      const response = await this.client.put<TimeOffPolicyApiModel>(
        `${this.policyUrl}/${policyId}/archive`,
        {note, archivedAt},
      )
      return mapPolicyFromApi(response.data)
    } catch (error) {
      reportCatchError('Error archiving time-off policy:', error)
      throw error
    }
  }

  async restorePolicy(policyId: string): Promise<TimeOffPolicy> {
    try {
      const response = await this.client.put<TimeOffPolicyApiModel>(
        `${this.policyUrl}/${policyId}/restore`,
      )
      return mapPolicyFromApi(response.data)
    } catch (error) {
      reportCatchError('Error restoring time-off policy:', error)
      throw error
    }
  }

  /**
   * Assignment Time Off tab — balances + history from Global catalogue policies
   * that cover this assignment.
   * GET /time-off-assignment/summary?assignmentId=&applicantId=
   */
  async getAssignmentSummary(
    assignmentId: string,
    applicantId?: string,
  ): Promise<AssignmentTimeOffSummaryApiModel> {
    try {
      const query = new URLSearchParams({assignmentId})
      if (applicantId) query.append('applicantId', applicantId)
      const response = await this.client.get<AssignmentTimeOffSummaryApiModel>(
        `${this.assignmentUrl}/summary?${query.toString()}`,
      )
      return {
        assignmentId: response.data.assignmentId,
        applicantId: response.data.applicantId,
        ...mapAssignmentTimeOffSummary(response.data),
        hireDate: response.data.hireDate ?? null,
      } as AssignmentTimeOffSummaryApiModel
    } catch (error) {
      reportCatchError('Error fetching assignment time-off summary:', error)
      throw error
    }
  }

  /**
   * Projected balance as of a date for Assignment → Calculate Time Off.
   * POST /time-off-assignment/project
   */
  async projectBalance(
    payload: ProjectTimeOffBalanceRequest,
  ): Promise<ProjectTimeOffBalanceResponse> {
    try {
      const response = await this.client.post<ProjectTimeOffBalanceResponse>(
        `${this.assignmentUrl}/project`,
        payload,
      )
      return response.data
    } catch (error) {
      reportCatchError('Error projecting time-off balance:', error)
      throw error
    }
  }

  /**
   * Submit a Pending time-off request (not synced to Ronos until approved).
   * POST /time-off-request
   */
  async createTimeOffRequest(
    payload: CreateTimeOffRequestPayload,
  ): Promise<TimeOffRequestApiModel> {
    try {
      const response = await this.client.post<TimeOffRequestApiModel>(this.requestUrl, payload)
      return response.data
    } catch (error) {
      reportCatchError('Error creating time-off request:', error)
      throw error
    }
  }

  /**
   * List time-off requests for an assignment / applicant.
   * GET /time-off-request?assignmentId=&applicantId=&status=
   */
  async listTimeOffRequests(params: {
    assignmentId?: string
    applicantId?: string
    status?: TimeOffRequestApiModel['status']
  }): Promise<TimeOffRequestApiModel[]> {
    try {
      const query = new URLSearchParams()
      if (params.assignmentId) query.append('assignmentId', params.assignmentId)
      if (params.applicantId) query.append('applicantId', params.applicantId)
      if (params.status) query.append('status', params.status)
      const suffix = query.toString() ? `?${query.toString()}` : ''
      const response = await this.client.get<TimeOffRequestApiModel[]>(
        `${this.requestUrl}${suffix}`,
      )
      return response.data ?? []
    } catch (error) {
      reportCatchError('Error listing time-off requests:', error)
      throw error
    }
  }

  /** POST /time-off-request/:id/approve */
  async approveTimeOffRequest(id: string): Promise<TimeOffRequestApiModel> {
    try {
      const response = await this.client.post<TimeOffRequestApiModel>(
        `${this.requestUrl}/${id}/approve`,
      )
      return response.data
    } catch (error) {
      reportCatchError('Error approving time-off request:', error)
      throw error
    }
  }

  /** POST /time-off-request/:id/deny */
  async denyTimeOffRequest(id: string, note?: string): Promise<TimeOffRequestApiModel> {
    try {
      const response = await this.client.post<TimeOffRequestApiModel>(
        `${this.requestUrl}/${id}/deny`,
        note ? {note} : {},
      )
      return response.data
    } catch (error) {
      reportCatchError('Error denying time-off request:', error)
      throw error
    }
  }

  /** POST /time-off-request/:id/cancel */
  async cancelTimeOffRequest(id: string): Promise<TimeOffRequestApiModel> {
    try {
      const response = await this.client.post<TimeOffRequestApiModel>(
        `${this.requestUrl}/${id}/cancel`,
      )
      return response.data
    } catch (error) {
      reportCatchError('Error cancelling time-off request:', error)
      throw error
    }
  }

  /** Loads categories + all policies for the landing/cache snapshot. */
  async loadCatalogue(): Promise<{
    categories: TimeOffCategory[]
    policies: TimeOffPolicy[]
  }> {
    const [categories, policies] = await Promise.all([this.listCategories(), this.listPolicies()])
    return {categories, policies}
  }
}

export const timeOffPoliciesApi = new TimeOffPoliciesApi()
